import { type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface KpiDelta {
  /** Displayed magnitude, e.g. `'8.2%'` or `'12s'`. */
  value: string;
  /** Arrow direction. */
  direction: 'up' | 'down';
  /**
   * Whether this movement is GOOD for the metric. Colour follows goodness, not
   * direction (design-reference §4.2.4): a down arrow on "Needs review" is good.
   */
  good: boolean;
}

export interface KpiTileProps {
  label: string;
  /** Pre-formatted value (tabular figures applied by the tile). */
  value: string;
  delta?: KpiDelta;
  /** Optional sparkline node (a `<Sparkline />`). */
  sparkline?: ReactNode;
  className?: string;
}

/**
 * A KPI tile: big tabular number + label + signed delta (arrow + colour + text)
 * + optional sparkline. The delta is never colour alone — the ▲/▼ glyph and the
 * number carry it too.
 */
export function KpiTile({ label, value, delta, sparkline, className }: KpiTileProps): React.JSX.Element {
  const deltaClass =
    delta === undefined
      ? undefined
      : `ds-kpi__delta--${delta.direction}-${delta.good ? 'good' : 'bad'}`;
  return (
    <div className={cx('ds-kpi', className)}>
      <div className="ds-kpi__label">{label}</div>
      <div className="ds-kpi__row">
        <span className="ds-kpi__value">{value}</span>
        {delta !== undefined ? (
          <span className={cx('ds-kpi__delta', deltaClass)}>
            <span aria-hidden="true">{delta.direction === 'up' ? '▲' : '▼'}</span> {delta.value}
            <span className="sr-only">
              {delta.direction === 'up' ? 'up' : 'down'} {delta.value},{' '}
              {delta.good ? 'improving' : 'worsening'}
            </span>
          </span>
        ) : null}
      </div>
      {sparkline}
    </div>
  );
}
